import React, { useState } from 'react';
import { Building2, Hotel, Car, Sun, Shirt, Zap, Package, Wheat, Factory, TrainFront, Server, Cpu } from 'lucide-react';
import { INDIA_PATH } from '../data/indiaPath';
import { SERVICE_CITIES } from '../data/serviceCities';

const ICON_MAP = { Building2, Hotel, Car, Sun, Shirt, Zap, Package, Wheat, Factory, TrainFront, Server, Cpu };

const VBW = 600;
const VBH = 640;

/**
 * Clean India map with segment-wise city markers.
 * Each marker = white/orange rounded chip with a lucide icon representing
 * the dominant project segment for that city. Hover → chip lifts, ring
 * expands, and a dark tooltip pill fades in with city + segment.
 */
export default function IndiaMap({ className = '' }) {
  const [hover, setHover] = useState(null);

  return (
    <div
      className={`relative flex items-center justify-center select-none ${className}`}
      data-testid="india-map-illustration"
    >
      {/* soft radial glow */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(closest-side, rgba(37,150,190,0.13) 0%, rgba(37,150,190,0.03) 55%, transparent 80%)',
        }}
      />

      {/* India outline */}
      <svg
        viewBox={`0 0 ${VBW} ${VBH}`}
        className="relative w-full h-full"
        role="img"
        aria-label="Map of India with Tricolite project locations"
      >
        <defs>
          <linearGradient id="india-fill-clean" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--blue)" stopOpacity="0.10" />
            <stop offset="100%" stopColor="var(--blue-deep)" stopOpacity="0.20" />
          </linearGradient>
        </defs>
        <path
          d={INDIA_PATH}
          fill="url(#india-fill-clean)"
          stroke="var(--blue-deep)"
          strokeOpacity="0.5"
          strokeWidth="1"
          strokeLinejoin="round"
          fillRule="evenodd"
        />
      </svg>

      {/* HTML overlay with icon chips — absolutely positioned by % of viewBox */}
      <div className="absolute inset-0 pointer-events-none">
        {SERVICE_CITIES.map((c, i) => {
          const Icon = ICON_MAP[c.icon] || Building2;
          const isHover = hover === i;
          const size = c.hq ? 38 : 30;
          const iconSize = c.hq ? 18 : 14;
          return (
            <div
              key={c.name}
              className="absolute"
              style={{
                left: `${(c.x / VBW) * 100}%`,
                top: `${(c.y / VBH) * 100}%`,
                transform: 'translate(-50%, -50%)',
                zIndex: isHover ? 30 : c.hq ? 20 : 10,
              }}
            >
              <button
                type="button"
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover((h) => (h === i ? null : h))}
                onFocus={() => setHover(i)}
                onBlur={() => setHover((h) => (h === i ? null : h))}
                aria-label={`${c.name} — ${c.segment}`}
                data-testid={`city-marker-${c.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={`pointer-events-auto relative flex items-center justify-center rounded-full transition-all duration-300 ease-out outline-none focus-visible:ring-2 focus-visible:ring-[var(--orange)] ${
                  c.hq
                    ? 'bg-[var(--orange)] text-white shadow-[0_6px_14px_-4px_rgba(232,90,26,0.55)]'
                    : 'bg-white text-[var(--blue-deep)] shadow-[0_4px_10px_-3px_rgba(14,22,32,0.18)] border border-[var(--line)]'
                } ${isHover ? 'scale-125' : 'hover:scale-110'}`}
                style={{ width: size, height: size }}
              >
                {/* Halo ring on hover / HQ */}
                <span
                  aria-hidden
                  className={`absolute inset-0 rounded-full transition-opacity duration-300 ${
                    isHover ? 'opacity-100' : 'opacity-0'
                  }`}
                  style={{
                    boxShadow: c.hq
                      ? '0 0 0 6px rgba(232,90,26,0.18)'
                      : '0 0 0 6px rgba(37,150,190,0.20)',
                  }}
                />
                <Icon
                  style={{ width: iconSize, height: iconSize }}
                  strokeWidth={2.2}
                  className={`transition-colors duration-300 ${
                    !c.hq && isHover ? 'text-[var(--orange)]' : ''
                  }`}
                />
              </button>
            </div>
          );
        })}
      </div>

      {/* Hover tooltip — floating above the active chip */}
      {hover !== null && (
        <div
          className="absolute pointer-events-none z-40"
          style={{
            left: `${(SERVICE_CITIES[hover].x / VBW) * 100}%`,
            top: `${(SERVICE_CITIES[hover].y / VBH) * 100}%`,
            transform: 'translate(-50%, calc(-100% - 26px))',
          }}
        >
          <div
            className="px-3.5 py-2 rounded-xl bg-[var(--ink)] text-white shadow-[0_10px_24px_-8px_rgba(14,22,32,0.4)] whitespace-nowrap"
            data-testid="city-tooltip"
          >
            <div className="text-[12px] font-bold leading-none">{SERVICE_CITIES[hover].name}</div>
            <div className="text-[10px] uppercase tracking-[0.12em] text-white/60 mt-1 leading-none">
              {SERVICE_CITIES[hover].segment}
            </div>
          </div>
          <div
            aria-hidden
            className="absolute left-1/2 -translate-x-1/2 -bottom-1 w-2 h-2 rotate-45 bg-[var(--ink)]"
          />
        </div>
      )}
    </div>
  );
}
